import React, { useMemo, useEffect, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import FigoLottie from '../../../common/FigoLottie';
import { ContactUsModal } from '../../../sideMenu/contactUs/ContactUsModal';

import { CLAIMS } from '../../../navigation/routes.constants';
import { useMixpanel } from '../../../../hooks';
import { EV5 } from '../../../../services/mixpanel';
import {
  getMinutesFromInitialTime,
  getDateBasicFormat,
} from '../../../../services/utils';
import { getAllBrandsData } from '../../../../actions';
import errorIcon from '../../../../assets/claimbot/submit-error.svg';
import successIcon from '../../../../assets/claimbot/submit-success.svg';
import loadingSpinner from '../../../../assets/lottieFiles/loading-blue.json';
import {
  CONTACT_US_STEPS,
} from '../../../sideMenu/contactUs/contactUs.constants';

import './ClaimSubmitted.css';

const ClaimSubmitted = ({ success, policyBrandName, selectedPolicy }) => {
  const { t } = useTranslation('fileClaimbot');
  const history = useHistory();
  const dispatch = useDispatch();
  const { sendEvent } = useMixpanel();
  const icon = success ? successIcon : errorIcon;
  const title = success ? t('claimSubmitted.success.title')
    : t('claimSubmitted.error.title');
  const [contactUsVisible, setContactUsVisible] = useState(false);

  const store = useSelector(({ billingPayments, fileClaims }) => ({
    billingPayments,
    fileClaims,
  }), shallowEqual);

  const {
    amount,
    claimsDetails,
    date,
    initialTime,
    policyNumber,
    billingPayments: { allBrandsData, loadingAllBrandsData },
  } = store;

  useEffect(() => {
    dispatch(getAllBrandsData());
  }, []);

  const lostDateFormated = getDateBasicFormat(+date, 'YYYY-MM-DD hh:mm:ss');
  const timeSpentBeforeSubmit = getMinutesFromInitialTime(initialTime);

  const pms = useMemo(() => {
    if (!allBrandsData || !selectedPolicy) {
      return '';
    }

    return allBrandsData.find((policy) => policy.PolicyNumber
      === selectedPolicy.PolicyNumber)?.PolicyManagementSystem;
  }, [allBrandsData]);

  const togleContactUsModalVisible = useCallback(() => {
    setContactUsVisible((prevState) => !prevState);
  }, []);

  useEffect(() => {
    if (allBrandsData && pms) {
      sendEvent(EV5({
        claimAmount: amount,
        claimData: store,
        claimDescription: claimsDetails,
        claimResult: success ? 'submitted' : 'error',
        dateOfLoss: lostDateFormated,
        pms,
        policyBrand: policyBrandName,
        policyNumber,
        timeSpentBeforeSubmit,
      }));
    }
  }, [
    amount,
    claimsDetails,
    pms,
    policyBrandName,
    policyNumber,
    success,
    timeSpentBeforeSubmit,
  ]);

  const description = useMemo(() => {
    if (success) {
      const onFinishPress = () => {
        history.push(CLAIMS);
      };

      return (
        <span>
          {t('claimSubmitted.success.toSeeTheStatus')}

          <button onClick={onFinishPress} type="button">
            {t('claimSubmitted.success.claims')}
          </button>

          {t('claimSubmitted.success.home')}
        </span>
      );
    }

    return (
      <span>
        {t('claimSubmitted.error.pleaseRetry')}

        <button onClick={togleContactUsModalVisible} type="button">
          {t('claimSubmitted.error.contactUs')}
        </button>

        {t('claimSubmitted.error.forMoreInformation')}

        <ContactUsModal
          companyStep={CONTACT_US_STEPS.HOME}
          onClose={togleContactUsModalVisible}
          show={contactUsVisible}
        />
      </span>
    );
  }, [success, t, contactUsVisible]);

  if (loadingAllBrandsData) {
    return (
      <FigoLottie
        animationData={loadingSpinner}
        height={30}
        width={30}
      />
    );
  }

  return (
    <div className="File-claimbot-claim-submitted-container">
      <img alt="" src={icon} />

      <h2>{title}</h2>

      {description}
    </div>
  );
};

export { ClaimSubmitted };
